import type { TreeOption } from 'naive-ui';

/**
 * 文件定位：
 * 1. 当前文件位于 `web-client/hooks/feature-tree` 目录；
 * 2. 负责设计树展开状态的纯数据计算；
 * 3. 不依赖组件实例与 Pinia store，避免把 UI 展开策略耦合到 `FeatureTree.vue` 中。
 */

/**
 * 读取树节点的标准字符串 key。
 *
 * @param node 设计树节点。
 * @returns 可用于展开状态比较的字符串 key。
 */
export function getFeatureTreeNodeKey(node: TreeOption): string {
    return node.key === undefined || node.key === null ? '' : String(node.key);
}

/**
 * 收集当前树的一级根节点 key。
 *
 * @param nodes 设计树节点列表。
 * @returns 根节点 key 列表。
 */
function collectRootTreeNodeKeys(nodes: readonly TreeOption[]): string[] {
    return nodes
        .map(getFeatureTreeNodeKey)
        .filter((key) => key.length > 0);
}

/**
 * 收集当前树中仍然存在的全部节点 key。
 *
 * @param nodes 设计树节点列表。
 * @returns 节点 key 列表。
 */
function collectTreeNodeKeys(nodes: readonly TreeOption[]): string[] {
    return nodes.flatMap((node) => {
        const nodeKey = getFeatureTreeNodeKey(node);
        // 递归收集子节点，保证刷新后只保留仍在当前树投影中的展开 key。
        const childKeys = Array.isArray(node.children)
            ? collectTreeNodeKeys(node.children)
            : [];

        return nodeKey.length > 0 ? [nodeKey, ...childKeys] : childKeys;
    });
}

/**
 * 查找目标节点的祖先 key 链。
 *
 * 设计说明：
 * 选中子节点时必须保证父级链路展开，否则滚动补偿找不到真实 DOM 节点。
 *
 * @param nodes 当前设计树节点。
 * @param targetKey 目标节点 key。
 * @param ancestorKeys 当前递归层已经累积的祖先 key。
 * @returns 目标节点的祖先 key 列表，未命中时返回 `null`。
 */
function findTreeNodeAncestorKeys(
    nodes: readonly TreeOption[],
    targetKey: string,
    ancestorKeys: readonly string[] = [],
): string[] | null {
    for (const node of nodes) {
        const nodeKey = getFeatureTreeNodeKey(node);
        if (nodeKey === targetKey) {
            return [...ancestorKeys];
        }

        const nextAncestorKeys = nodeKey.length > 0
            ? [...ancestorKeys, nodeKey]
            : ancestorKeys;
        // 命中子树后立即返回，避免继续遍历兄弟节点造成不必要的递归开销。
        const matchedAncestorKeys = Array.isArray(node.children)
            ? findTreeNodeAncestorKeys(node.children, targetKey, nextAncestorKeys)
            : null;
        if (matchedAncestorKeys) {
            return matchedAncestorKeys;
        }
    }

    return null;
}

/**
 * 解析下一轮展开节点 key。
 *
 * 设计说明：
 * 1. 根节点保持默认展开，符合设计树首次加载后的浏览习惯；
 * 2. 数据刷新时保留仍存在的展开 key，避免选中草图引用后父特征被刷新折叠；
 * 3. 当前选中节点的祖先链强制展开，保证选中态与滚动定位不会丢失。
 *
 * @param nodes 当前设计树节点。
 * @param previousExpandedKeys 刷新前的展开 key。
 * @param selectedKey 当前选中节点 key。
 * @returns 去重后的展开 key 列表。
 */
export function resolveFeatureTreeExpandedKeys(
    nodes: readonly TreeOption[],
    previousExpandedKeys: readonly string[],
    selectedKey: string,
): string[] {
    // 用当前树投影收口旧展开键，避免已删除节点残留在受控 expandedKeys 中。
    const existingKeySet = new Set(collectTreeNodeKeys(nodes));
    const stableExpandedKeys = previousExpandedKeys.filter((key) => existingKeySet.has(key));
    // 选中子节点时展开祖先链，保证树刷新后用户仍能看到当前选中项。
    const selectedAncestorKeys = selectedKey.length > 0
        ? findTreeNodeAncestorKeys(nodes, selectedKey) ?? []
        : [];

    return [
        ...new Set([
            ...collectRootTreeNodeKeys(nodes),
            ...stableExpandedKeys,
            ...selectedAncestorKeys,
        ]),
    ];
}
